// External
import { GdataAuthProvider } from '@gleamer/auth0-extension';
import { AutoLogoutModal, GdataRoutes } from '@gleamer/gdata-extension';
import { QueryContextProvider } from '@gleamer/query-context-extension';
import {
  CommandsManager,
  ExtensionManager,
  HotkeysManager,
  ServiceProvidersManager,
  ServicesManager,
  SystemContextProvider,
  ViewportRefsProvider,
} from '@ohif/core';
import i18n from '@ohif/i18n';
import {
  CineProvider,
  DialogProvider,
  ManagedDialog,
  Modal as ModalNext,
  ModalProvider,
  NotificationProvider,
  ThemeWrapper as ThemeWrapperNext,
  TooltipProvider,
  UserAuthenticationProvider as UserAuthenticationProviderNext,
  ViewportDialogProvider,
  ViewportGridProvider,
} from '@ohif/ui-next';
import { AppConfigProvider } from '@state';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { I18nextProvider } from 'react-i18next';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ShepherdJourneyProvider } from 'react-shepherd';
import appInit from './appInit.js';
import createRoutes from './routes';
import Compose from './routes/Mode/Compose';

let commandsManager: CommandsManager,
  extensionManager: ExtensionManager,
  servicesManager: ServicesManager,
  hotkeysManager: HotkeysManager;

function Gdata({ config, defaultExtensions, defaultModes }) {
  const [init, setInit] = useState(null);
  useEffect(() => {
    const run = async () => {
      appInit(config, defaultExtensions, defaultModes)
        .then(setInit)
        .catch(console.error);
    };

    run();
  }, []);

  if (!init) {
    return null;
  }

  // Set above for named export
  commandsManager = init.commandsManager;
  extensionManager = init.extensionManager;
  servicesManager = init.servicesManager;
  hotkeysManager = init.hotkeysManager;

  // Set appConfig
  const appConfigState = init.appConfig;
  const { routerBasename, modes, dataSources, oidc, showStudyList } =
    appConfigState;

  const serviceProvidersManager: ServiceProvidersManager =
    init.serviceProvidersManager;

  const {
    uiDialogService,
    uiModalService,
    uiNotificationService,
    uiViewportDialogService,
    viewportGridService,
    cineService,
    userAuthenticationService,
    customizationService,
  } = servicesManager.services;

  // OHIF 3.9 provider set (mirrors platform/app/src/App.tsx) plus Gleamer's
  // GdataAuthProvider + QueryContextProvider. SnackbarProvider was replaced by
  // ui-next NotificationProvider; ThemeWrapperNext/TooltipProvider/
  // ShepherdJourneyProvider are new in 3.9. 3.11 deleted @ohif/ui's own
  // ThemeWrapper and ToolboxProvider (the ui -> ui-next migration completed
  // upstream); ToolboxProvider had no consumers here, so it was simply dropped.
  const providers = [
    [AppConfigProvider, { value: appConfigState }],
    // OHIF 3.10 moved UserAuthentication to @ohif/ui-next (upstream PrivateRoute
    // reads that context). A SINGLE provider must own the core singleton: running
    // both @ohif/ui and @ohif/ui-next providers gave each its own `user` reducer
    // state while both registered `_getUser`/`_setUser` on the same service, so
    // Auth0Handler wrote the token into one provider's state but read it back
    // (null) from the other → `getAuthorizationHeader()` returned `{}` → 401 on
    // observations/characterisations. All Gleamer consumers now read the core
    // singleton (`servicesManager.services.userAuthenticationService`) directly.
    [UserAuthenticationProviderNext, { service: userAuthenticationService }],
    [
      GdataAuthProvider,
      { UserAuthenticationService: userAuthenticationService },
    ],
    [QueryContextProvider],
    [I18nextProvider, { i18n }],
    [ThemeWrapperNext],
    // OHIF 3.10 added SystemContextProvider (@ohif/core); components now read
    // servicesManager/commandsManager/etc. via useSystem(). Missing it makes
    // useSystem() return null and destructuring it throws in the viewport tree.
    [
      SystemContextProvider,
      { servicesManager, commandsManager, extensionManager, hotkeysManager },
    ],
    // OHIF 3.11 added ViewportRefsProvider (@ohif/core). `useViewportRef`
    // (called by extensions/cornerstone OHIFCornerstoneViewport) THROWS when the
    // provider is absent, which blanks the whole viewport subtree via the error
    // boundary — same failure family as the 3.10 SystemContextProvider gap.
    [ViewportRefsProvider],
    // OHIF 3.10: the ViewportGrid moved to @ohif/ui-next (upstream ViewportGrid.tsx
    // consumes ui-next's useViewportGrid). Both ui and ui-next providers call
    // service.setServiceImplementation, so only one can own the core service —
    // use the ui-next one to match upstream. Gleamer components were migrated to
    // import useViewportGrid from @ohif/ui-next accordingly.
    [ViewportGridProvider, { service: viewportGridService }],
    [ViewportDialogProvider, { service: uiViewportDialogService }],
    [CineProvider, { service: cineService }],
    [NotificationProvider, { service: uiNotificationService }],
    [TooltipProvider],
    [DialogProvider, { service: uiDialogService, dialog: ManagedDialog }],
    [ModalProvider, { service: uiModalService, modal: ModalNext }],
    [ShepherdJourneyProvider],
  ];

  // Register providers contributed by extensions via ServiceProvidersManager
  // (e.g. cornerstone's ViewportActionCornersProvider) — the 3.9 no-fork hook.
  const providersFromManager = Object.entries(serviceProvidersManager.providers);
  if (providersFromManager.length > 0) {
    providersFromManager.forEach(([serviceName, provider]) => {
      providers.push([provider, { service: servicesManager.services[serviceName] }]);
    });
  }

  const CombinedProviders = ({ children }) =>
    Compose({ components: providers, children });

  const authRoutes = null;

  // Should there be a generic call to init on the extension manager?
  customizationService.init(extensionManager);

  // Use config to create routes
  const appRoutes = createRoutes({
    modes,
    dataSources,
    extensionManager,
    servicesManager,
    commandsManager,
    hotkeysManager,
    routerBasename,
    showStudyList,
  });

  return (
    <BrowserRouter basename={routerBasename}>
      <CombinedProviders>
        <AutoLogoutModal />
        <Routes>
          <Route
            path="/*"
            element={<GdataRoutes servicesManager={servicesManager} />}
          />
          <Route path="ohif/*" element={appRoutes} />
        </Routes>
      </CombinedProviders>
    </BrowserRouter>
  );
}

Gdata.propTypes = {
  config: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({
      routerBasename: PropTypes.string.isRequired,
      oidc: PropTypes.array,
      whiteLabeling: PropTypes.object,
      extensions: PropTypes.array,
    }),
  ]).isRequired,
  /* Extensions that are "bundled" or "baked-in" to the application.
   * These would be provided at build time as part of they entry point. */
  defaultExtensions: PropTypes.array,
};

Gdata.defaultProps = {
  config: {
    /**
     * Relative route from domain root that OHIF instance is installed at.
     * For example:
     *
     * Hosted at: https://ohif.org/where-i-host-the/viewer/
     * Value: `/where-i-host-the/viewer/`
     * */
    routerBaseName: '/',
    /**
     *
     */
    showLoadingIndicator: true,
    showStudyList: true,
    oidc: [],
    extensions: [],
  },
  defaultExtensions: [],
};

export default Gdata;

export { commandsManager, extensionManager, servicesManager };
